import { useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelectedOrganization } from './useSelectedOrganization';
import { showToast } from 'helpers/showToast';
import { DateTimeStringFormat } from 'constants/time-formats';
import { addDays, differenceInCalendarDays, format, isValid, parse, parseISO } from 'date-fns';
import pluralize from 'pluralize';
import { OrganizationSubscriptionType, SubscriptionStatus } from 'models/graphql';
import { useOrganizationPlan } from './useOrganizationPlan';

const PAID_GRACE_PERIOD = 14;
const FREE_TRIAL_GRACE_PERIOD = 7;
const QTY_DAYS_REMAINING_TRIAL_TO_START_SENDING_NOTIFICATIONS = 9;
const getSubscriptionNotificationToast = (
  isOwner: boolean,
  subscription: OrganizationSubscriptionType,
  isTrial: boolean
): { summary: string; severity: 'warn'; detail: JSX.Element } => {
  const { status, trialExpiryDateUtc, upgradedDateUtc, nextBillingDateUtc } = subscription;

  const urlToRedirect = `/settings/manage-subscription?status=${status}`;
  const LinkComponent = () => (
    <Link to={urlToRedirect} target="_blank">
      upgrade
    </Link>
  );

  if (status === SubscriptionStatus.PAST_DUE) {
    const toastTitle = upgradedDateUtc ? 'Your subscription has expired' : 'Your free trial is over';
    const gracePeriod = addDays(
      parse(isTrial ? trialExpiryDateUtc : nextBillingDateUtc, DateTimeStringFormat, new Date()),
      isTrial ? FREE_TRIAL_GRACE_PERIOD : PAID_GRACE_PERIOD
    );
    const formattedDate = format(gracePeriod, "do 'of' MMMM");

    if (isOwner) {
      return {
        summary: toastTitle,
        severity: 'warn',
        detail: (
          <>
            Your Queue will be paused on the {formattedDate}. Please <LinkComponent /> to a paid subscription to keep
            using SmarterQueue.
          </>
        )
      };
    }
    return {
      summary: toastTitle,
      severity: 'warn',
      detail: (
        <>
          Your Queue will be paused on the {formattedDate}. Please ask your Organization Owner to upgrade to a paid
          subscription.
        </>
      )
    };
  }

  if (status === SubscriptionStatus.EXPIRED) {
    const toastTitle = upgradedDateUtc ? 'Your subscription has expired' : 'Your trial is over';
    if (isOwner) {
      return {
        summary: toastTitle,
        severity: 'warn',
        detail: (
          <>
            Your Queue is now paused. Please <LinkComponent /> to a paid subscription to keep using SmarterQueue.
          </>
        )
      };
    }
    return {
      summary: toastTitle,
      severity: 'warn',
      detail: <>Your Queue is now paused. Please ask your Organization Owner to upgrade to a paid subscription.</>
    };
  }

  if (status === SubscriptionStatus.TRIAL) {
    const currentDate = new Date();

    const trialExpireDate = parse(trialExpiryDateUtc, DateTimeStringFormat, currentDate);
    const remainingTrialDays = differenceInCalendarDays(trialExpireDate, currentDate);
    const toastTitle =
      remainingTrialDays === 0
        ? 'Your Trial ends today'
        : `Your trial ends in ${remainingTrialDays} ${pluralize('day', remainingTrialDays)}`;

    if (isOwner) {
      return {
        summary: toastTitle,
        severity: 'warn',
        detail: (
          <>
            Please <LinkComponent /> to a paid subscription to keep using SmarterQueue.
          </>
        )
      };
    }

    return {
      summary: toastTitle,
      severity: 'warn',
      detail: <>Please ask your Organization Owner to upgrade to a paid subscription.</>
    };
  }

  // Should not reach here
  return {
    summary: '',
    severity: 'warn',
    detail: <></>
  };
};

type OrgNotificationInfo = {
  [key: string]: string | null;
};
const LOCAL_STORAGE_NOTIFICATION_KEY = 'subscriptionNotifications';

export const useSubscriptionStatus = () => {
  const location = useLocation();
  const { selectedOrganization } = useSelectedOrganization();
  const { isTrial, isOwner } = useOrganizationPlan();
  const isoDateString = selectedOrganization?.subscription.trialExpiryDateUtc.replace(' ', 'T') + 'Z';
  const trialExpireDate = parseISO(isoDateString);
  const userSubscription = selectedOrganization?.subscription;
  const currentDate = new Date();
  const remainingTrialDays = differenceInCalendarDays(trialExpireDate, currentDate);
  const orgId = selectedOrganization?.id;

  const shouldSendNotification = useCallback(() => {
    if (!orgId) return;
    const stringifiedLocalStorageObj = localStorage.getItem(LOCAL_STORAGE_NOTIFICATION_KEY);
    const isCloseToExpire = remainingTrialDays <= QTY_DAYS_REMAINING_TRIAL_TO_START_SENDING_NOTIFICATIONS;

    if (isTrial && !isCloseToExpire) {
      return false;
    }

    if (userSubscription?.status === SubscriptionStatus.PAID) {
      return false;
    }

    const parsedInfo = stringifiedLocalStorageObj ? JSON.parse(stringifiedLocalStorageObj) : { [orgId]: null };
    const currentOrgLastDateSent = parseISO(parsedInfo[orgId]);
    const diffDays = isValid(currentOrgLastDateSent)
      ? Math.abs(differenceInCalendarDays(currentOrgLastDateSent, new Date()))
      : 1;
    const shouldSendNotification = isCloseToExpire ? isCloseToExpire && diffDays > 0 : diffDays > 0;
    return shouldSendNotification;
  }, [remainingTrialDays, userSubscription?.status, isTrial, orgId]);

  const sendToast = useCallback(() => {
    if (!userSubscription || !orgId) return;
    const stringifiedLocalStorageObj = localStorage.getItem(LOCAL_STORAGE_NOTIFICATION_KEY);
    showToast(getSubscriptionNotificationToast(isOwner, userSubscription, isTrial));
    const parsedLocalStorageObjs: OrgNotificationInfo = stringifiedLocalStorageObj
      ? JSON.parse(stringifiedLocalStorageObj)
      : {};

    parsedLocalStorageObjs[orgId] = new Date().toISOString();
    localStorage.setItem(LOCAL_STORAGE_NOTIFICATION_KEY, JSON.stringify(parsedLocalStorageObjs));
  }, [isOwner, orgId, userSubscription, isTrial]);

  useEffect(() => {
    if (shouldSendNotification()) {
      sendToast();
    }
  }, [location, shouldSendNotification, sendToast]);

  return {
    isAccessBlocked: selectedOrganization?.suspended,
    isTemporallySuspended: selectedOrganization?.spamLocked
  };
};
